/**
 * Feature usage shortcuts.
 *
 * Semantic helpers over `track("feature_usage", { featureId, featureName, action })`.
 * Pick the verb that describes what happened — the dashboard funnel maps each
 * action onto a lifecycle stage (a *viewed* feature is Discovered, a *used* one
 * is Adopted). No hidden state, no guessing: you send exactly what you call.
 */
import { TrackFeature } from "../../features/core/track-feature";

export type FeatureAction = "viewed" | "used" | "completed" | "abandoned";

export class FeatureTracker {
  constructor(public readonly client: TrackFeature) {}

  /** Emit a `feature_usage` event with an explicit action (defaults to "used"). */
  record(featureId: string, featureName: string, action: FeatureAction = "used", properties?: Record<string, unknown>) {
    return this.client.trackUsage({ featureId, featureName, action, properties });
  }

  /** Visitor opened / looked at the feature. Funnel → Discovered + Viewed. */
  viewed(featureId: string, featureName: string, properties?: Record<string, unknown>) {
    return this.record(featureId, featureName, "viewed", properties);
  }

  /** Visitor used the feature. Funnel → Adopted. */
  used(featureId: string, featureName: string, properties?: Record<string, unknown>) {
    return this.record(featureId, featureName, "used", properties);
  }

  /** Visitor finished the feature's job successfully. Funnel → Completed. */
  completed(featureId: string, featureName: string, properties?: Record<string, unknown>) {
    return this.record(featureId, featureName, "completed", properties);
  }

  /** Visitor started but gave up. Funnel → Abandonment. */
  abandoned(featureId: string, featureName: string, properties?: Record<string, unknown>) {
    return this.record(featureId, featureName, "abandoned", properties);
  }

  /** Create / register a feature event (advanced — see `client.create`). */
  create(userId: string, action: string, featureName: string, properties?: Record<string, unknown>) {
    return this.client.create({ userId, action, properties: { featureName, ...properties } });
  }
}
